import React, { useEffect, useState } from 'react';
import { apiClient } from '../../api/client';

interface Chapter {
  id: string;
  name: string;
}

interface ChapterDropdownProps {
  value: string;
  freeText: string;
  onChange: (chapter: string) => void;
  onFreeTextChange: (text: string) => void;
  disabled?: boolean;
  error?: string;
}

const styles: Record<string, React.CSSProperties> = {
  select: {
    padding: '0.5rem 0.75rem',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    fontSize: '0.85rem',
    backgroundColor: '#fff',
    width: '100%',
  },
  selectError: {
    padding: '0.5rem 0.75rem',
    border: '1px solid #d32f2f',
    borderRadius: '4px',
    fontSize: '0.85rem',
    backgroundColor: '#fff',
    width: '100%',
  },
  input: {
    padding: '0.5rem 0.75rem',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    fontSize: '0.85rem',
    marginTop: '0.5rem',
    width: '100%',
  },
  fieldError: {
    fontSize: '0.75rem',
    color: '#d32f2f',
    marginTop: '0.15rem',
  },
};

export function ChapterDropdown({
  value,
  freeText,
  onChange,
  onFreeTextChange,
  disabled = false,
  error,
}: ChapterDropdownProps) {
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function fetchChapters() {
      try {
        const data = await apiClient<Chapter[]>('/chapters');
        if (!cancelled) {
          setChapters(data);
        }
      } catch {
        // If chapters fetch fails, just show an empty list
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    fetchChapters();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div>
      <select
        style={error ? styles.selectError : styles.select}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled || loading}
        aria-label="Chapter affiliation"
      >
        <option value="">Select chapter...</option>
        {chapters.map((chapter) => (
          <option key={chapter.id} value={chapter.name}>
            {chapter.name}
          </option>
        ))}
        <option value="Other">Other</option>
      </select>
      {value === 'Other' && (
        <input
          type="text"
          style={styles.input}
          value={freeText}
          onChange={(e) => onFreeTextChange(e.target.value)}
          placeholder="Enter chapter name..."
          disabled={disabled}
          maxLength={200}
          aria-label="Custom chapter name"
        />
      )}
      {error && <span style={styles.fieldError}>{error}</span>}
    </div>
  );
}
